import { action, makeAutoObservable, observable, runInAction } from 'mobx';

export type ConnectionStatus =
  | 'disconnected'
  | 'connecting'
  | 'connected'
  | 'reconnecting'
  | 'failed';

// Retry configuration
const MAX_RETRIES = 5;
const INITIAL_RETRY_DELAY = 1000; // 1 second
const MAX_RETRY_DELAY = 30000; // 30 seconds

export class WebSocketStore {
  connectionStatus: ConnectionStatus = 'disconnected';
  reconnectAttempts: number = 0;
  lastError: string | null = null;
  eventHandlers: Map<string, ((data: any) => void)[]> = new Map();
  websocket: WebSocket | null = null;
  reconnectTimer: NodeJS.Timeout | null = null;
  isManualDisconnect: boolean = false;

  constructor() {
    makeAutoObservable(this, {
      eventHandlers: observable,
      websocket: observable,
      reconnectTimer: observable,
    });
  }

  /**
   * Register an event handler for a specific event type
   */
  @action
  on(eventType: string, handler: (data: any) => void) {
    if (!this.eventHandlers.has(eventType)) {
      this.eventHandlers.set(eventType, []);
    }
    this.eventHandlers.get(eventType)!.push(handler as (data: any) => void);
  }

  /**
   * Unregister an event handler for a specific event type
   */
  @action
  off(eventType: string, handler: (data: any) => void) {
    const handlers = this.eventHandlers.get(eventType);
    if (handlers) {
      const index = handlers.indexOf(handler);
      if (index > -1) {
        handlers.splice(index, 1);
      }
    }
  }

  /**
   * Emit an event to all registered handlers
   */
  @action
  emit(eventType: string, data: any) {
    const handlers = this.eventHandlers.get(eventType);
    if (handlers) {
      handlers.forEach(handler => {
        try {
          handler(data);
        } catch (error) {
          console.error(`Error in event handler for ${eventType}:`, error);
        }
      });
    }
  }

  /**
   * Calculate retry delay with exponential backoff
   */
  private calculateRetryDelay(attempt: number): number {
    const delay = INITIAL_RETRY_DELAY * Math.pow(2, attempt);
    return Math.min(delay, MAX_RETRY_DELAY);
  }

  /**
   * Connect to WebSocket with retry mechanism
   */
  @action
  async connect(wsUrl: string) {
    // Don't connect if already connected or connecting
    if (this.connectionStatus === 'connected' || this.connectionStatus === 'connecting') {
      return;
    }

    // Clear any existing reconnect timer
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    this.isManualDisconnect = false;
    this.reconnectAttempts = 0;
    await this.attemptConnection(wsUrl, 0);
  }

  /**
   * Attempt WebSocket connection with retry logic
   */
  @action
  private async attemptConnection(wsUrl: string, attempt: number) {
    if (this.isManualDisconnect) {
      return;
    }

    // Update status
    if (attempt === 0) {
      this.connectionStatus = 'connecting';
    } else {
      this.connectionStatus = 'reconnecting';
      this.reconnectAttempts = attempt;
    }
    this.lastError = null;

    try {
      // Close existing connection if any
      if (this.websocket) {
        this.websocket.close();
        this.websocket = null;
      }

      // Create new WebSocket connection
      const ws = new WebSocket(wsUrl);
      this.websocket = ws;

      // Set up event handlers
      ws.onopen = () => {
        runInAction(() => {
          this.connectionStatus = 'connected';
          this.reconnectAttempts = 0;
          this.lastError = null;
        });
        this.emit('connected', {});
      };

      ws.onclose = () => {
        runInAction(() => {
          this.websocket = null;
        });

        // Don't reconnect if manually disconnected
        if (this.isManualDisconnect) {
          runInAction(() => {
            this.connectionStatus = 'disconnected';
          });
          return;
        }

        // Attempt reconnection
        if (attempt < MAX_RETRIES) {
          const delay = this.calculateRetryDelay(attempt);
          runInAction(() => {
            this.connectionStatus = 'reconnecting';
            this.lastError = `Connection closed. Reconnecting in ${delay / 1000}s...`;
          });

          this.reconnectTimer = setTimeout(() => {
            this.attemptConnection(wsUrl, attempt + 1);
          }, delay);
        } else {
          runInAction(() => {
            this.connectionStatus = 'failed';
            this.lastError = 'Failed to connect after multiple attempts';
          });
          this.emit('connection_failed', { error: this.lastError });
        }
      };

      ws.onerror = error => {
        runInAction(() => {
          this.lastError = 'WebSocket connection error';
        });
        this.emit('error', { error });
      };

      ws.onmessage = event => {
        try {
          const data = JSON.parse(event.data);
          this.handleMessage(data);
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };
    } catch (error: any) {
      runInAction(() => {
        this.lastError = error.message || 'Failed to create WebSocket connection';
      });

      // Retry if not exceeded max attempts
      if (attempt < MAX_RETRIES) {
        const delay = this.calculateRetryDelay(attempt);
        this.reconnectTimer = setTimeout(() => {
          this.attemptConnection(wsUrl, attempt + 1);
        }, delay);
      } else {
        runInAction(() => {
          this.connectionStatus = 'failed';
          this.lastError = 'Failed to connect after multiple attempts';
        });
        this.emit('connection_failed', { error: this.lastError });
      }
    }
  }

  /**
   * Handle incoming WebSocket messages
   */
  @action
  private handleMessage(data: any) {
    const messageType = data.type;

    // Emit to registered handlers
    this.emit(messageType, data);

    // Also emit to generic 'message' handlers
    if (messageType !== 'message') {
      this.emit('message', data);
    }
  }

  /**
   * Send message to WebSocket server
   */
  @action
  sendMessage(message: any) {
    if (this.websocket && this.websocket.readyState === WebSocket.OPEN) {
      try {
        this.websocket.send(JSON.stringify(message));
        return true;
      } catch (error) {
        console.error('Error sending WebSocket message:', error);
        return false;
      }
    } else {
      console.warn('WebSocket is not connected. Cannot send message.');
      return false;
    }
  }

  /**
   * Disconnect from WebSocket
   */
  @action
  disconnect() {
    this.isManualDisconnect = true;

    // Clear reconnect timer
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    // Close WebSocket connection
    if (this.websocket) {
      this.websocket.close();
      this.websocket = null;
    }

    this.connectionStatus = 'disconnected';
    this.reconnectAttempts = 0;
    this.lastError = null;
  }

  /**
   * Reset store state
   */
  @action
  reset() {
    this.disconnect();
    this.eventHandlers.clear();
  }
}
